/**
 * Feature-flag source switch.
 *
 * Controls where feature-flag values come from at runtime:
 * - `static`  — the existing `window.__FLAGS__` system (generated from
 *   `ci/flags/flags.matrix.json`).
 * - `harness` — flags fetched from Harness FME (Split) via the browser SDK.
 *
 * The value is read from the `window.__FLAGS_SOURCE__` runtime global so it can
 * be flipped per environment by swapping the generated runtime asset, without a
 * rebuild. Any unknown/absent value falls back to the safe `static` default.
 */
export const FLAGS_SOURCES = ["static", "harness"] as const;

export type FlagsSource = (typeof FLAGS_SOURCES)[number];

export const DEFAULT_FLAGS_SOURCE: FlagsSource = "static";

declare global {
  var __FLAGS_SOURCE__: string | undefined;
}

const isFlagsSource = (value: unknown): value is FlagsSource =>
  typeof value === "string" && FLAGS_SOURCES.includes(value as FlagsSource);

export const getFlagsSource = (): FlagsSource => {
  const raw = globalThis.__FLAGS_SOURCE__;
  return isFlagsSource(raw) ? raw : DEFAULT_FLAGS_SOURCE;
};

export const isHarnessFlagsSource = (): boolean =>
  getFlagsSource() === "harness";
